/** Fractured stone: intersect broad planes instead of displacing a sphere. */
import * as THREE from 'three';
import { range, type Rng } from '@hyperlight/shared';
import { V, shade } from './primitives';
import type { C3 } from './types';

type Face = THREE.Vector3[];
const EPS = 1e-8;

/** Clip a convex solid and close the new fracture with one planar face. */
function cut(faces:Face[], normal:THREE.Vector3, distance:number):Face[] {
  const out:Face[]=[], rim:THREE.Vector3[]=[];
  const unique=(points:THREE.Vector3[])=>points.filter((p,i)=>!points.slice(0,i).some(q=>q.distanceToSquared(p)<EPS*EPS));
  for(const face of faces) {
    const clipped:Face=[];
    for(let i=0;i<face.length;i++) {
      const a=face[i], b=face[(i+1)%face.length], da=normal.dot(a)-distance, db=normal.dot(b)-distance;
      if(da<=EPS)clipped.push(a);
      if((da<-EPS&&db>EPS)||(da>EPS&&db<-EPS)) {
        const p=a.clone().lerp(b,da/(da-db));clipped.push(p);rim.push(p);
      }
    }
    const clean=unique(clipped);if(clean.length>=3)out.push(clean);
  }
  const cap=unique(rim);
  if(cap.length>=3) {
    const center=cap.reduce((sum,p)=>sum.add(p),V(0,0,0)).divideScalar(cap.length);
    const x=new THREE.Vector3().crossVectors(normal,Math.abs(normal.y)<.9?V(0,1,0):V(1,0,0)).normalize();
    const y=new THREE.Vector3().crossVectors(normal,x).normalize();
    cap.sort((a,b)=>Math.atan2(a.clone().sub(center).dot(y),a.clone().sub(center).dot(x))
      -Math.atan2(b.clone().sub(center).dot(y),b.clone().sub(center).dot(x)));
    out.push(cap);
  }
  return out;
}

/** Four related profiles: block, low slab, wedge and shouldered outcrop.
 * The buried skirt is real closed geometry, not a raised flat bottom. */
export function buildBoulder(rng:Rng, stone:C3, profile=0):THREE.BufferGeometry {
  const slab=profile===1, wedge=profile===2;
  const sx=range(rng,slab?1.30:1.0,slab?1.55:1.28), sz=range(rng,.83,1.10);
  const height=range(rng,slab?.42:.82,slab?.60:1.06), bottom=-range(rng,.42,.58);
  const corners=[V(-sx,bottom,-sz),V(sx,bottom,-sz),V(sx,bottom,sz),V(-sx,bottom,sz),
    V(-sx,height*1.3,-sz),V(sx,height*1.3,-sz),V(sx,height*1.3,sz),V(-sx,height*1.3,sz)];
  let faces:Face[]=[[0,3,2,1],[4,5,6,7],[0,1,5,4],[1,2,6,5],[2,3,7,6],[3,0,4,7]].map(ids=>ids.map(i=>corners[i]));
  // Orient the initial box outward. Clipping preserves each face's winding.
  for(const f of faces)if(f[1].clone().sub(f[0]).cross(f[2].clone().sub(f[0])).dot(f[0])<0)f.reverse();
  // Irregular side planes erase the box's orthogonal outline. Their crown
  // cuts meet at staggered heights, giving large facets without a level lid.
  const phase=range(rng,0,Math.PI*2), sides=7;
  for(let i=0;i<sides;i++) {
    const angle=phase+i*Math.PI*2/sides+range(rng,-.18,.18);
    faces=cut(faces,V(Math.cos(angle)/sx,range(rng,.08,.28)/height,Math.sin(angle)/sz),range(rng,.82,1.0));
  }
  faces=cut(faces,V(range(rng,-.28,.24)/sx,1/height,range(rng,-.25,.25)/sz),1);
  for(let i=0;i<6;i++) {
    const angle=phase+(i+.45)*Math.PI/3+range(rng,-.22,.22), rake=range(rng,.7,1.35);
    faces=cut(faces,V(Math.cos(angle)*rake/sx,1/height,Math.sin(angle)*rake/sz),range(rng,1.08,1.32));
  }
  if(wedge)faces=cut(faces,V(1/sx,.8/height,.14/sz),.97);
  if(profile===3)faces=cut(faces,V(.32/sx,.65/height,1/sz),1.04);
  // Geological shear breaks the box's right angles while retaining real flat
  // fracture planes. A plane remains a plane under this affine transform.
  const lean=range(rng,-.24,.24), crossLean=range(rng,-.14,.18), skew=range(rng,-.14,.14);
  const transformed=new Map<THREE.Vector3,THREE.Vector3>();
  faces=faces.map(face=>face.map(p=>{
    let q=transformed.get(p);
    if(!q){q=V(p.x+lean*p.y+skew*p.z,p.y,p.z+crossLean*p.y);transformed.set(p,q);}
    return q;
  }));
  const positions:number[]=[],normals:number[]=[],colors:number[]=[];
  let radius=0;
  for(const face of faces) {
    const n=face[1].clone().sub(face[0]).cross(face[2].clone().sub(face[0])).normalize();
    // One pigment per fracture plane; no noisy triangle-by-triangle color.
    const c=shade(stone,range(rng,.96,1.04)*(.88+.12*Math.max(0,n.y)));
    for(let i=1;i<face.length-1;i++) {
      const b=face[i],d=face[i+1];
      if(b.clone().sub(face[0]).cross(d.clone().sub(face[0])).lengthSq()<1e-14)continue;
      for(const p of [face[0],b,d]) {
        positions.push(p.x,p.y,p.z);normals.push(n.x,n.y,n.z);colors.push(...c);
        radius=Math.max(radius,Math.hypot(p.x,p.z));
      }
    }
  }
  const g=new THREE.BufferGeometry();
  g.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));
  g.setAttribute('normal',new THREE.Float32BufferAttribute(normals,3));
  g.setAttribute('color',new THREE.Float32BufferAttribute(colors,3));
  g.setAttribute('sway',new THREE.Float32BufferAttribute(new Float32Array(positions.length/3),1));
  g.computeBoundingBox();g.computeBoundingSphere();
  g.userData={rockProfile:['block','slab','wedge','shoulder'][profile],rockFootprintRadius:radius};
  return g;
}
